import { api } from '../api/endpoints.js';
import { backButton, mainButton, haptic } from '../tg.js';

// Pack picker. Each card shows cover + name + progress dots.
// If user has an active session, that pack's card shows actual unlocked count
// + "RESUME" label. Other cards show "5 STAGES" hint.
//
// Tap card → POST /sessions → game scene.
export const packsScene = {
  async mount(root, ctx) {
    // Native BackButton → return to menu
    this._offBack = backButton.show(() => {
      haptic('light');
      ctx.goto('menu');
    });
    mainButton.hide();

    root.innerHTML = `
      <div class="packs">
        <div class="packs-header">
          <div class="packs-sub">PICK YOUR</div>
          <h2 class="packs-title">WAIFU</h2>
        </div>
        <div class="packs-grid" id="packs-grid"></div>
      </div>`;

    const grid = root.querySelector('#packs-grid');

    // Fetch packs + current session in parallel
    const [packsRes, sessionRes] = await Promise.allSettled([
      api.listPacks(),
      api.getCurrentSession()
    ]);

    if (packsRes.status === 'rejected') {
      grid.innerHTML = `<div class="packs-empty error">Failed to load: ${packsRes.reason?.message || 'unknown'}</div>`;
      return;
    }

    const packs = packsRes.value?.packs || [];
    const activeSession = sessionRes.status === 'fulfilled' ? sessionRes.value?.session : null;
    const activePackId = activeSession?.pack?.id || null;

    if (packs.length === 0) {
      grid.innerHTML = `<div class="packs-empty">No packs yet — run <code>go run main.go seed</code></div>`;
      return;
    }

    for (const p of packs) {
      const cover = p.cover_image_url || p.cover_url;
      const total = p.total_slots ?? 5;
      const isActive = p.id === activePackId;
      const unlocked = isActive ? (activeSession.unlocked_count ?? 0) : 0;
      const status = isActive
        ? (activeSession.status === 'pending_respawn' ? 'YOU DIED' : 'RESUME')
        : null;

      const dotsHTML = Array.from({ length: total }, (_, i) =>
        `<span class="d ${i < unlocked ? 'on' : ''}"></span>`
      ).join('');

      const card = document.createElement('button');
      card.className = `pack-card ${isActive ? 'active' : ''}`;
      card.innerHTML = `
        <div class="pack-img" style="background-image:url('${cover}')"></div>
        ${status ? `<div class="pack-flag ${activeSession.status === 'pending_respawn' ? 'danger' : ''}">${status}</div>` : ''}
        <div class="pack-info">
          <div class="pack-name">${p.name}</div>
          <div class="pack-dots">
            ${dotsHTML}
            <span class="pack-progress">${unlocked}/${total}</span>
          </div>
        </div>`;

      card.onclick = () => onPick(card, p, isActive);
      grid.appendChild(card);
    }

    async function onPick(card, pack, isActive) {
      haptic('medium');
      card.classList.add('loading');
      try {
        if (isActive) {
          // Resume existing session — use the data we already have
          ctx.state.pack = {
            id: activeSession.pack.id,
            name: activeSession.pack.name,
            cover_url: activeSession.pack.cover_image_url,
            cover_image_url: activeSession.pack.cover_image_url,
            total_slots: activeSession.pack.total_slots
          };
          ctx.state.activeSession = activeSession;
          ctx.state.startWithRespawnModal = activeSession.status === 'pending_respawn';
          ctx.goto('game');
          return;
        }

        const start = await api.startSession(pack.id);
        ctx.state.pack = pack;
        ctx.state.activeSession = {
          id: start.session_id,
          status: 'active',
          unlocked_count: 0,
          unlocked_slots: [],
          respawn_cost: null,
          can_respawn: false,
          balance: ctx.state.user?.star_balance ?? 0,
          pack: {
            id: pack.id,
            name: pack.name,
            cover_image_url: pack.cover_image_url || pack.cover_url,
            total_slots: pack.total_slots
          }
        };
        ctx.state.startWithRespawnModal = false;
        ctx.goto('game');
      } catch (e) {
        card.classList.remove('loading');
        haptic('error');
        if (e.status === 409) {
          alert('You already have an active session. Returning to menu.');
          ctx.goto('menu');
        } else {
          alert(`Failed to start: ${e.message}`);
        }
      }
    }
  },

  async destroy() {
    this._offBack?.();
  }
};
